import React from "react";
import { Link } from "react-router-dom";
import { formatDate } from "../lib/api";
import { CAT_LABEL } from "../lib/categories";
import { Zap, Star, PlayCircle } from "lucide-react";

const FALLBACK = "/brand/logo-512.png";

function Img({ src, alt, className }) {
  return (
    <img
      src={src || FALLBACK}
      alt={alt}
      loading="lazy"
      onError={(e) => { e.target.src = FALLBACK; }}
      className={className}
    />
  );
}

export function Kicker({ a }) {
  const cat = CAT_LABEL[a.category];
  return (
    <span className="kicker text-[11px]">
      {a.language === "hi" && cat ? cat.name_hi : cat ? cat.name : a.category}
    </span>
  );
}

function Badges({ a }) {
  return (
    <>
      {a.breaking && <span className="bg-r24-red text-white text-[10px] font-bold uc px-1.5 py-0.5">Breaking</span>}
      {a.exclusive && <span className="bg-black text-white text-[10px] font-bold uc px-1.5 py-0.5">Exclusive</span>}
      {a.video_url && <span className="bg-black/80 text-white text-[10px] font-bold uc px-1.5 py-0.5 inline-flex items-center gap-1"><PlayCircle size={11}/>Video</span>}
    </>
  );
}

// Big hero card
export function HeroCard({ a }) {
  const hi = a.language === "hi";
  return (
    <Link to={`/article/${a.slug}`} data-testid={`hero-card-${a.slug}`} className="group block relative overflow-hidden bg-black h-full min-h-[320px] lg:min-h-[480px]">
      <Img src={a.featured_image} alt={a.title} className="card-img absolute inset-0 w-full h-full object-cover opacity-85" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent" />
      <div className="absolute bottom-0 p-5 md:p-8 w-full">
        <div className="flex gap-2 mb-3 flex-wrap items-center"><Badges a={a} /><Kicker a={a} /></div>
        <h2 className={`font-head text-white text-2xl md:text-4xl lg:text-5xl font-bold leading-[1.05] group-hover:text-red-200 transition-colors ${hi ? "lang-hi" : ""}`}>
          {a.title}
        </h2>
        {a.subtitle && <p className={`text-gray-200 mt-3 text-sm md:text-base max-w-3xl ${hi ? "lang-hi" : ""}`}>{a.subtitle}</p>}
        <div className="text-gray-300 text-xs mt-3 font-mono2">{a.author} · {formatDate(a.published_at, a.language)}</div>
      </div>
    </Link>
  );
}

// Standard vertical card
export function NewsCard({ a, compact = false }) {
  const hi = a.language === "hi";
  return (
    <Link to={`/article/${a.slug}`} data-testid={`news-card-${a.slug}`} className="group block bg-white">
      <div className="relative overflow-hidden aspect-video bg-gray-100">
        <Img src={a.featured_image} alt={a.title} className="card-img w-full h-full object-cover" />
        <div className="absolute top-0 left-0 flex gap-1 p-2"><Badges a={a} /></div>
      </div>
      <div className="pt-3">
        <Kicker a={a} />
        <h3 className={`font-head font-bold leading-tight mt-1 link-head ${compact ? "text-base" : "text-lg md:text-xl"} ${hi ? "lang-hi" : ""}`}>
          {a.title}
        </h3>
        {!compact && a.excerpt && <p className={`text-gray-600 text-sm mt-2 line-clamp-2 ${hi ? "lang-hi" : ""}`}>{a.excerpt}</p>}
        <div className="text-gray-500 text-[11px] mt-2 font-mono2">{formatDate(a.published_at, a.language)} · {a.location || a.author}</div>
      </div>
    </Link>
  );
}

// Horizontal list item (small thumb + headline)
export function ListCard({ a, index }) {
  const hi = a.language === "hi";
  return (
    <Link to={`/article/${a.slug}`} data-testid={`list-card-${a.slug}`} className="group flex gap-3 py-3 border-b border-gray-200 last:border-0 items-start">
      {typeof index === "number" && <span className="font-head text-2xl font-bold text-r24-red leading-none w-6 shrink-0">{index + 1}</span>}
      <div className="flex-1 min-w-0">
        <h4 className={`font-head font-bold text-[15px] leading-snug link-head ${hi ? "lang-hi" : ""}`}>{a.title}</h4>
        <div className="text-gray-500 text-[11px] mt-1 font-mono2">{formatDate(a.published_at, a.language)}</div>
      </div>
      {a.featured_image && (
        <div className="w-20 h-16 shrink-0 overflow-hidden bg-gray-100">
          <Img src={a.featured_image} alt={a.title} className="card-img w-full h-full object-cover" />
        </div>
      )}
    </Link>
  );
}

export { Img };
